import { useMemo, useRef, type HTMLAttributes } from 'react';
import { useRouter } from 'next/compat/router';
import useSWRImmutable from 'swr/immutable';

import { screens } from '@virginexperiencedays/tailwind/screens';
import { useWindowWidth } from '@virginexperiencedays/hooks';

import {
  Carousel,
  type ResponsivePerPage,
} from '@virginexperiencedays/components-v2/src/layout/Carousel';
import { ProductCardRenderer } from '../../shared/ProductCardRenderer';
import { PdpModules } from '../../../../libs/tracking/pdp';
import { DataTestId } from '../VariationPage/PageUI';

import type { Product } from '@virginexperiencedays/products';
import type { DyCampaignApiBody } from '../../../../pages/api/dy/recommendations/campaign';

const DY_PDP_YMAL_CAMPAIGN = process.env.NEXT_PUBLIC_DY_PDP_YMAL_CAMPAIGN;

const DEFAULT_ITEMS: ResponsivePerPage = {
  mobile: 'small',
  tablet: 5,
  desktop: 4,
  wide: 5,
};

const DEFAULT_ITEMS_COMPACT: ResponsivePerPage = {
  mobile: 'small',
  tablet: 4,
  desktop: 3,
  wide: 4,
};

export type Props = {
  sku: string;
  isGoogleShopping?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const ProductRecommendationWidget = ({ sku, isGoogleShopping, ...props }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const { width } = useWindowWidth();

  const { recommendations, isLoading, error } = useFetchFromDY(sku, DY_PDP_YMAL_CAMPAIGN, true);

  const perPage = isGoogleShopping ? DEFAULT_ITEMS_COMPACT : DEFAULT_ITEMS;
  const trackingModule = isGoogleShopping
    ? PdpModules.ProductRecommendationsGs
    : PdpModules.ProductRecommendations;

  const itemsToRender = useMemo(() => {
    if (!isLoading && !error && Array.isArray(recommendations)) {
      return recommendations.filter((item: any) => typeof item?.slug === 'string');
    }

    const perPageNValues = Object.values(perPage).filter((v) => !isNaN(Number(v))) as number[];
    const largestPerPage = Math.max(...perPageNValues);
    return Array.from({ length: largestPerPage }, (_, i) => ({ slug: `skeleton${i + 1}` }));
  }, [recommendations, error, isLoading, perPage]);

  if (isLoading || error || !Array.isArray(recommendations)) {
    return <LoadingStateCarousel perPage={perPage} width={width} />;
  }
  return (
    <div data-testid={DataTestId.YouMayAlsoLike} ref={ref} {...props}>
      <Carousel
        perPage={perPage}
        showNavigationArrows={width >= screens.lg}
        isProductCarousel
        withHeading
      >
        {itemsToRender.map((item: { slug: string }, i: number) => (
          <ProductCardRenderer
            render="carousel-card"
            key={item.slug}
            index={i}
            item={item}
            singleImage
            trackingModule={trackingModule}
          />
        ))}
      </Carousel>
    </div>
  );
};

type LoadingStateCarouselProps = {
  perPage: ResponsivePerPage;
  width: number;
};

const LoadingStateCarousel = ({ perPage, width }: LoadingStateCarouselProps) => {
  return (
    <Carousel
      perPage={perPage}
      showNavigationArrows={width >= screens.lg}
      isProductCarousel
      withHeading
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <ProductCardRenderer render="skeleton" key={i} index={i} />
      ))}
    </Carousel>
  );
};

type UseFetchFromDY = (
  sku: string,
  campaign: string,
  inView: boolean
) => {
  recommendations: Product[];
  isLoading: boolean;
  error: boolean;
};

const useFetchFromDY: UseFetchFromDY = (sku, campaign, inView) => {
  const router = useRouter();

  const apiRoute = '/api/dy/recommendations/campaign';
  const params = createParams(sku, campaign, router.asPath);

  const { data, isLoading, error } = useSWRImmutable(
    router.isReady && inView ? [apiRoute, params] : null,
    fetcher
  );

  let recommendations: Product[] = [];
  if (typeof data === 'object' && data !== null && 'recommendations' in data) {
    recommendations = Array.isArray((data as { recommendations: Product[] }).recommendations)
      ? (data as { recommendations: Product[] }).recommendations
      : [];
  }

  return { recommendations, isLoading, error: !!error };
};

/**
 * Create the params object for the DY API
 */
const createParams = (sku: string, campaign: string, location: string): DyCampaignApiBody => ({
  selector: {
    names: [campaign],
  },
  context: {
    page: {
      data: [sku],
      location,
    },
  },
});

/**
 * Fetcher function for SWR and `/api/dy/recommendations/campaign`
 */
const fetcher = async ([route, params]: [string, Object]): Promise<unknown> => {
  const res = await fetch(route, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    throw new Error('Error fetching recommendations');
  }

  return res.json();
};
